import React, {
  type ComponentProps,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { ModalContext } from './context';
import { type ModalName, modals } from './modals';

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modalState, setModalState] = useState<
    {
      name: ModalName;
      props: ComponentProps<(typeof modals)[ModalName]>;
    }[]
  >([]);

  const openModal = useCallback(
    <T extends ModalName>(
      name: T,
      props: React.ComponentProps<(typeof modals)[T]>,
    ) => {
      const modal = modals[name];

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!modal) {
        console.warn(`Modal with name "${name}" does not exist.`);

        return;
      }

      setModalState((prev) => [...prev, { name, props }]);
    },
    [],
  );

  const closeModal = useCallback((name: ModalName) => {
    setModalState((prev) => prev.filter((modal) => modal.name !== name));
  }, []);

  const contextValue = useMemo(
    () => ({
      openModal,
      closeModal,
    }),
    [openModal, closeModal],
  );

  return (
    <ModalContext value={contextValue}>
      {children}
      {modalState.map(({ name, props }, index) => {
        const ModalComponent = modals[name];

        return (
          // @ts-expect-error The component may not accept props
          <ModalComponent key={`${name}-${index}`} {...(props ?? {})} />
        );
      })}
    </ModalContext>
  );
};
